import React, { useRef, useMemo } from 'react';
import { extend, useFrame } from '@react-three/fiber';
import { Tube, shaderMaterial } from '@react-three/drei';
import * as THREE from 'three';

// 1. Define the custom shader material declaratively
const RiverMaterial = shaderMaterial(
  { time: 0, color: new THREE.Color('#FFFFFF'), intensity: 1.0 },
  // Vertex Shader
  `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment Shader
  `
    uniform float time;
    uniform vec3 color;
    uniform float intensity;
    varying vec2 vUv;

    void main() {
      float flow = fract(vUv.x * 2.0 - time * 0.5);
      float core = smoothstep(0.5, 0.45, abs(vUv.y - 0.5));
      float pulse = sin(flow * 3.14159 * 2.0) * 0.5 + 0.5;
      float line = core * pulse;
      float glow = smoothstep(0.5, 0.0, abs(vUv.y - 0.5)) * 0.5;
      vec3 finalColor = color * (line + glow) * intensity;
      float fade = smoothstep(0.0, 0.1, vUv.x) * smoothstep(1.0, 0.9, vUv.x);
      
      gl_FragColor = vec4(finalColor, (line + glow) * fade * intensity);
    }
  `
);

// Extend it to use in JSX
extend({ RiverMaterial });

const SynapseRiver = ({ start, end, sharedWordCount = 0 }) => {
  const materialRef = useRef();

  const curve = useMemo(() => {
    const startVec = new THREE.Vector3(...start);
    const endVec = new THREE.Vector3(...end);
    const midPoint = new THREE.Vector3().addVectors(startVec, endVec).multiplyScalar(0.5);
    const distance = startVec.distanceTo(endVec);
    midPoint.y += distance * 0.2;
    return new THREE.CatmullRomCurve3([startVec, midPoint, endVec]);
  }, [start, end]);

  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.time = clock.getElapsedTime();
      // Use a power function for a more dramatic visual falloff
      // Weak connections will be much dimmer, strong ones much brighter
      const intensityRatio = sharedWordCount / 200;
      materialRef.current.intensity = 0.2 + Math.pow(intensityRatio, 2) * 4.0;
    }
  });

  return (
    <mesh>
      <tubeGeometry args={[curve, 32, 0.05, 8, false]} />
      <riverMaterial 
        ref={materialRef} 
        blending={THREE.AdditiveBlending} 
        transparent={true}
        depthWrite={false}
      />
    </mesh>
  );
};

export default SynapseRiver;
